import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  Stars as StarsIcon,
  Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon,
  FlashOn as FlashOnIcon,
  StoreMallDirectory as StoreIcon,
  LocalBar as BarIcon,
  Face as BeautyIcon,
  ShoppingCart as MarketIcon,
  Build as ToolsIcon,
  BusinessCenter as GeneralIcon,
  InfoOutlined as InfoIcon,
} from '@material-ui/icons';

import StoreContext from '../../Store/context';
import { callApi } from '../../../utils/useApi';
import { updateCompanySegment } from '../../../services/companyService';
import { SEGMENTS_DATA, normalizeSegment } from '../../Home/trialOnboardingData';

const ICON_COMPONENTS = {
  StoreMallDirectory: StoreIcon,
  LocalBar: BarIcon,
  Face: BeautyIcon,
  ShoppingCart: MarketIcon,
  Build: ToolsIcon,
  BusinessCenter: GeneralIcon,
};

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
    maxWidth: 760,
  },
  header: {
    background: 'linear-gradient(135deg, #0C2D51 0%, #17497F 60%, #1e3a8a 100%)',
    color: '#ffffff',
    padding: theme.spacing(3.5, 4, 3, 4),
    position: 'relative',
  },
  headerCloseBtn: {
    position: 'absolute',
    top: 14,
    right: 14,
    color: 'rgba(255, 255, 255, 0.75)',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      color: '#ffffff',
    },
  },
  badgePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '4px 12px',
    borderRadius: 20,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: 0.5,
    cursor: 'pointer',
    border: 'none',
    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.2s ease',
    '&:hover': {
      filter: 'brightness(0.92)',
      transform: 'translateY(-1px)',
    },
  },
  progressBarWrapper: {
    marginTop: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    borderRadius: 12,
    padding: theme.spacing(1.5, 2),
    backdropFilter: 'blur(4px)',
  },
  progressTrack: {
    width: '100%',
    height: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    borderRadius: 4,
    overflow: 'hidden',
    marginTop: 6,
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
    borderRadius: 4,
    transition: 'width 0.4s ease',
  },
  content: {
    padding: theme.spacing(3, 4),
    backgroundColor: '#f8fafc',
    maxHeight: '52vh',
    overflowY: 'auto',
  },
  stepCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: theme.spacing(1.8, 2),
    marginBottom: theme.spacing(1.5),
    border: '1.5px solid #e2e8f0',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: '#3b82f6',
      boxShadow: '0 6px 18px -3px rgba(15, 23, 42, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  stepCardDone: {
    backgroundColor: '#f0fdf4',
    borderColor: '#bbf7d0',
    '&:hover': {
      borderColor: '#86efac',
    },
  },
  stepCardDuxia: {
    borderColor: '#e9d5ff',
    backgroundColor: '#faf5ff',
    '&:hover': {
      borderColor: '#c084fc',
    },
  },
  checkbox: {
    width: 26,
    height: 26,
    borderRadius: 8,
    border: '2px solid #cbd5e1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    flexShrink: 0,
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#3b82f6',
    },
  },
  checkboxChecked: {
    backgroundColor: '#10b981',
    borderColor: '#10b981',
    color: '#ffffff',
  },
  actionBtn: {
    borderRadius: 10,
    fontWeight: 700,
    fontSize: '0.8rem',
    textTransform: 'none',
    padding: '6px 14px',
    flexShrink: 0,
    marginLeft: theme.spacing(2),
  },
  duxiaTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 10,
    background: '#f3e8ff',
    color: '#7c3aed',
    fontWeight: 800,
    letterSpacing: 0.5,
    marginLeft: 8,
  },
  footer: {
    padding: theme.spacing(2, 4),
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  footerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: '#64748b',
    fontSize: '0.82rem',
  },
  footerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  primaryBtn: {
    borderRadius: 12,
    fontWeight: 700,
    padding: '10px 24px',
    background: 'linear-gradient(135deg, #0C2D51 0%, #17497F 100%)',
    color: '#ffffff',
    boxShadow: '0 4px 12px rgba(23, 73, 127, 0.3)',
    '&:hover': {
      background: 'linear-gradient(135deg, #08213b 0%, #123963 100%)',
    },
  },
}));

const FirstAccessOnboardingModal = ({ open, onClose }) => {
  const classes = useStyles();
  const history = useHistory();
  const { credencials, openChatbotWithPrompt, setCredencials } = useContext(StoreContext);

  const companyId = credencials?.company || credencials?.companyData?.id || 'default';
  const companyName =
    credencials?.companyData?.pseudonimo ||
    credencials?.companyData?.name ||
    credencials?.companyName ||
    'Sua Empresa';

  const initialSegmentFromCreds = normalizeSegment(
    credencials?.companyData?.segment || credencials?.segment
  );

  // Selected Segment State
  const [selectedSegmentKey, setSelectedSegmentKey] = useState(() => {
    const saved = localStorage.getItem(`duzz_onboarding_seg_${companyId}`);
    return saved && SEGMENTS_DATA[saved] ? saved : initialSegmentFromCreds;
  });

  // Completed steps state
  const [completedSteps, setCompletedSteps] = useState(() => {
    try {
      const saved = localStorage.getItem(`duzz_onboarding_progress_${companyId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  const [hasCatalogAddon, setHasCatalogAddon] = useState(() => {
    const fromCreds =
      credencials?.companyData?.addon_ids?.includes('catalog') ||
      credencials?.companyData?.has_catalog_addon;
    return Boolean(fromCreds);
  });

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  // Sync segment with credencials if not saved
  useEffect(() => {
    const saved = localStorage.getItem(`duzz_onboarding_seg_${companyId}`);
    if (!saved && initialSegmentFromCreds) {
      setSelectedSegmentKey(initialSegmentFromCreds);
    }
  }, [companyId, initialSegmentFromCreds]);

  // Listen for real-time progress and segment updates from lateral guide
  useEffect(() => {
    const handleProgressUpdate = (e) => {
      if (e?.detail?.companyId === companyId && e?.detail?.completedSteps) {
        setCompletedSteps((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(e.detail.completedSteps)) return prev;
          return e.detail.completedSteps;
        });
      }
    };
    const handleSegmentUpdate = (e) => {
      const incomingCompany = e?.detail?.companyId;
      const seg = e?.detail?.segmentKey || e?.detail?.segment;
      if ((!incomingCompany || String(incomingCompany) === String(companyId)) && seg) {
        setSelectedSegmentKey((prev) => {
          const norm = normalizeSegment(seg);
          if (prev === norm) return prev;
          return norm;
        });
      }
    };
    window.addEventListener('duzz:onboarding_progress_updated', handleProgressUpdate);
    window.addEventListener('duzz:onboarding_seg_updated', handleSegmentUpdate);
    return () => {
      window.removeEventListener('duzz:onboarding_progress_updated', handleProgressUpdate);
      window.removeEventListener('duzz:onboarding_seg_updated', handleSegmentUpdate);
    };
  }, [companyId]);

  // Initial load and safe sync with API
  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    try {
      const saved = localStorage.getItem(`duzz_onboarding_progress_${companyId}`);
      if (saved) {
        setCompletedSteps(JSON.parse(saved));
      }
    } catch (e) { }

    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    (async () => {
      try {
        const res = await callApi({ url: '/onboarding/status', method: 'get' }, credencials);
        if (res && res.data) {
          const d = res.data;
          if (d.has_catalog_addon !== undefined) {
            setHasCatalogAddon(Boolean(d.has_catalog_addon));
          }
          const autoChecked = {
            categories: d.categories_count > 0,
            products: d.products_count > 0,
            stock_drinks: d.products_count > 0,
            resale_products: d.products_count > 0,
            parts_stock: d.products_count > 0,
            xml_import: d.products_count > 0,
            services: d.services_count > 0,
            services_combos: Boolean(d.has_service_combos),
            services_labor: d.services_count > 0,
            attendants: d.users_count > 0,
            waiter_user: d.users_count > 0,
            first_client: d.customers_count > 0,
            cashier_management: d.cash_registers_count > 0,
            first_sale: d.sales_count > 0,
            quick_pos: d.sales_count > 0,
            first_comanda: d.sales_sessions_count > 0,
            budget_comanda: d.sales_sessions_count > 0,
            catalog: Boolean(d.catalog_enabled),
            agenda_setup: d.schedules_count > 0,
            delivery_settings: Boolean(d.has_delivery_tax),
            duxia_customer_analysis: Boolean(d.has_customer_analysis),
            duxia_product_description: Boolean(d.has_product_description),
            duxia_brand_palette: Boolean(d.has_brand_palette),
            duxia_eval: Boolean(d.has_chat_message),
          };
          setCompletedSteps((prev) => {
            const next = { ...prev, ...autoChecked };
            try {
              localStorage.setItem(`duzz_onboarding_progress_${companyId}`, JSON.stringify(next));
              window.dispatchEvent(
                new CustomEvent('duzz:onboarding_progress_updated', {
                  detail: { companyId, completedSteps: next },
                })
              );
            } catch (err) { }
            return next;
          });
        }
      } catch (err) {
        // fail silently
      }
    })();
  }, [open, companyId, credencials]);

  // Current segment configuration
  const segmentConfig = useMemo(() => {
    const raw = SEGMENTS_DATA[selectedSegmentKey] || SEGMENTS_DATA['Outro'];
    const filteredSteps = raw.steps.filter((s) => {
      if ((s.requireAddon === 'catalog' || s.id === 'catalog') && !hasCatalogAddon) {
        return false;
      }
      return true;
    });
    return {
      ...raw,
      steps: filteredSteps,
    };
  }, [selectedSegmentKey, hasCatalogAddon]);

  const SegmentIcon = ICON_COMPONENTS[segmentConfig.icon] || GeneralIcon;

  // Progress metrics
  const totalSteps = segmentConfig.steps.length;
  const completedCount = segmentConfig.steps.filter((s) => completedSteps[s.id]).length;
  const progressPercent = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  const handleSelectSegment = (segmentKey) => {
    setSelectedSegmentKey(segmentKey);
    localStorage.setItem(`duzz_onboarding_seg_${companyId}`, segmentKey);
    window.dispatchEvent(
      new CustomEvent('duzz:onboarding_seg_updated', {
        detail: { companyId, segmentKey },
      })
    );
    setMenuAnchorEl(null);
    updateCompanySegment(companyId, segmentKey, credencials, setCredencials);
  };

  const handleToggleStep = (stepId, e) => {
    if (e) e.stopPropagation();
    setCompletedSteps((prev) => {
      const next = { ...prev, [stepId]: !prev[stepId] };
      try {
        localStorage.setItem(`duzz_onboarding_progress_${companyId}`, JSON.stringify(next));
        window.dispatchEvent(
          new CustomEvent('duzz:onboarding_progress_updated', {
            detail: { companyId, completedSteps: next },
          })
        );
      } catch (err) { }
      return next;
    });
  };

  const dismissAndClose = useCallback(() => {
    if (companyId && companyId !== 'default') {
      localStorage.setItem(`duzz_first_access_onboarding_${companyId}`, 'true');
    }
    if (onClose) {
      onClose();
    }
  }, [companyId, onClose]);

  const handleExecuteStep = (step) => {
    dismissAndClose();
    if (step.type === 'duxia' && step.prompt) {
      if (typeof openChatbotWithPrompt === 'function') {
        openChatbotWithPrompt(step.prompt);
      }
    } else if (step.route) {
      history.push(step.route);
    }
  };

  const handleStartFirstPending = () => {
    const firstPending = segmentConfig.steps.find((s) => !completedSteps[s.id]);
    if (firstPending) {
      handleExecuteStep(firstPending);
    } else {
      dismissAndClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={dismissAndClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      {/* Header Banner */}
      <Box className={classes.header}>
        <IconButton
          size="small"
          className={classes.headerCloseBtn}
          onClick={dismissAndClose}
          aria-label="fechar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5} pr={4}>
          <button
            type="button"
            className={classes.badgePill}
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            style={{
              backgroundColor: segmentConfig.badgeBg,
              color: segmentConfig.badgeColor,
            }}
          >
            <SegmentIcon style={{ fontSize: 14 }} />
            <span>{segmentConfig.tag}</span>
            <ExpandMoreIcon style={{ fontSize: 14, opacity: 0.8 }} />
          </button>
        </Box>

        <Typography variant="h5" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
          Boas-vindas à Duzz, {companyName}! 🎉
        </Typography>

        <Typography
          variant="body2"
          style={{ color: 'rgba(255, 255, 255, 0.85)', marginTop: 4, fontWeight: 500 }}
        >
          {segmentConfig.headline} — Preparamos os passos recomendados para alavancar sua operação.
        </Typography>

        {/* Progress Bar */}
        <Box className={classes.progressBarWrapper}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
              <StarsIcon style={{ fontSize: 17, color: '#fbbf24' }} />
              <Typography variant="caption" style={{ fontWeight: 700, color: '#ffffff' }}>
                Progresso de Configuração Inicial
              </Typography>
            </Box>
            <Typography variant="caption" style={{ fontWeight: 800, color: '#ffffff' }}>
              {completedCount} de {totalSteps} concluídos ({progressPercent}%)
            </Typography>
          </Box>
          <div className={classes.progressTrack}>
            <div className={classes.progressFill} style={{ width: `${progressPercent}%` }} />
          </div>
        </Box>

        {/* Segment Selection Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={() => setMenuAnchorEl(null)}
          keepMounted
        >
          {Object.entries(SEGMENTS_DATA).map(([key, config]) => {
            const ItemIcon = ICON_COMPONENTS[config.icon] || GeneralIcon;
            return (
              <MenuItem
                key={key}
                selected={key === selectedSegmentKey}
                onClick={() => handleSelectSegment(key)}
                style={{
                  fontSize: '0.85rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 16px',
                }}
              >
                <ItemIcon style={{ fontSize: 18, color: config.badgeColor }} />
                <span style={{ fontWeight: key === selectedSegmentKey ? 700 : 500 }}>
                  {config.label}
                </span>
              </MenuItem>
            );
          })}
        </Menu>
      </Box>

      {/* Content: Step Cards */}
      <DialogContent className={classes.content}>
        <Box mb={2}>
          <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#334155' }}>
            Missões recomendadas para o seu primeiro acesso:
          </Typography>
        </Box>

        {segmentConfig.steps.map((step, idx) => {
          const isDone = Boolean(completedSteps[step.id]);
          const isDuxia = step.type === 'duxia' || step.type === 'duxia_feature';

          return (
            <Box
              key={step.id}
              className={`${classes.stepCard} ${isDone ? classes.stepCardDone : ''} ${isDuxia && !isDone ? classes.stepCardDuxia : ''
                }`}
              onClick={() => handleExecuteStep(step)}
            >
              <Box display="flex" alignItems="center" style={{ flex: 1, minWidth: 0 }}>
                <div
                  className={`${classes.checkbox} ${isDone ? classes.checkboxChecked : ''}`}
                  onClick={(e) => handleToggleStep(step.id, e)}
                  title={isDone ? 'Marcar como pendente' : 'Marcar como concluído'}
                >
                  {isDone && <CheckIcon style={{ fontSize: 16 }} />}
                </div>

                <Box style={{ flex: 1, minWidth: 0, paddingRight: 8 }}>
                  <Typography
                    variant="subtitle2"
                    style={{
                      fontWeight: 700,
                      color: isDone ? '#166534' : '#1e293b',
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>
                      {idx + 1}. {step.title}
                    </span>
                    {isDuxia && (
                      <span className={classes.duxiaTag}>
                        <ChatIcon style={{ fontSize: 10 }} /> DuxIA
                      </span>
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    style={{
                      color: isDone ? '#4b5563' : '#64748b',
                      fontSize: '0.82rem',
                      marginTop: 2,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant={isDone ? 'outlined' : 'contained'}
                color={isDone ? 'default' : 'primary'}
                className={classes.actionBtn}
                endIcon={
                  isDuxia ? (
                    <ChatIcon style={{ fontSize: 14 }} />
                  ) : (
                    <ArrowForwardIcon style={{ fontSize: 14 }} />
                  )
                }
                onClick={(e) => {
                  e.stopPropagation();
                  handleExecuteStep(step);
                }}
              >
                {isDone ? 'Revisar' : step.actionText}
              </Button>
            </Box>
          );
        })}
      </DialogContent>

      {/* Footer */}
      <DialogActions className={classes.footer}>
        <Box className={classes.footerInfo}>
          <InfoIcon fontSize="small" style={{ color: '#3b82f6' }} />
          <span>
            Este checklist de <strong>Primeiros Passos</strong> continua sempre disponível no
            carrossel da página inicial para acompanhar seu avanço.
          </span>
        </Box>

        <Box className={classes.footerActions}>
          <Button onClick={dismissAndClose} color="default" style={{ fontWeight: 600 }}>
            Explorar o Painel
          </Button>

          <Button
            variant="contained"
            className={classes.primaryBtn}
            onClick={handleStartFirstPending}
            endIcon={<FlashOnIcon />}
          >
            {completedCount === totalSteps ? 'Concluir Onboarding' : 'Começar Agora'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FirstAccessOnboardingModal;
